import { Box } from "@mui/material";
import { htmlToMarkdown, markdownToHtml } from "@ssf/components";
import { Image } from "@tiptap/extension-image";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table";
import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { forwardRef, useEffect } from "react";

import { Toolbar } from "./index.js";

const extensions = [
  StarterKit.configure({
    listKeymap: false,
    underline: false,
    trailingNode: false,
  }),
  Table,
  TableCell,
  TableHeader,
  TableRow,
  Image.configure({
    HTMLAttributes: {
      class: "editor-image",
    },
    allowBase64: true,
    inline: true,
  }),
];

export const Editor = forwardRef<
  Element,
  {
    markdown: string;
    onChange?: (markdown: string) => void;
    onUpdate?: (html: string) => void;
    paddingHorizontal?: string | number;
  }
>(function InternalEditor(
  { markdown, onChange, onUpdate, paddingHorizontal = 2 },
  ref,
) {
  const editor = useEditor({
    extensions,
    shouldRerenderOnTransaction: true,
    parseOptions: {
      preserveWhitespace: "full",
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onUpdate?.(html);
      onChange &&
        htmlToMarkdown(html).then(({ value }) => {
          if (typeof value === "string") {
            onChange(value);
          }
        });
    },
  });

  useEffect(() => {
    if (editor) {
      markdownToHtml(markdown).then(({ value }) => {
        if (typeof value === "string") {
          editor.commands.setContent(value, {
            emitUpdate: false,
            parseOptions: {
              preserveWhitespace: "full",
            },
          });
        }
      });
    }
  }, [editor, markdown]);

  return (
    <Box
      sx={{
        h1: {
          fontSize: 24,
        },
        table: {
          width: "100%",
        },
        "li > p, tr > * > p": {
          m: 0,
        },
        img: {
          maxWidth: "100%",
          height: "auto",
        },
      }}
    >
      {editor && (
        <Toolbar
          editor={editor}
          sx={{
            borderTop: ({ palette }) => `1px solid ${palette.divider}`,
            borderBottom: ({ palette }) => `1px solid ${palette.divider}`,
          }}
        />
      )}
      <Box
        ref={ref}
        sx={{
          py: 2,
          px: paddingHorizontal,
          "[contenteditable]": {
            outline: "none",
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
});
